import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronDown, Check, X, ArrowRight, ArrowLeft, Sparkles, Compass, RotateCcw, MessageCircle, ListChecks, CalendarClock } from 'lucide-react';

/** Prenotazione della configurazione guidata (stesso link usato dal sito flusia.it). */
const SETUP_CALL_BOOKING_URL = 'https://app.flusia.it/book/luca-bosso/consulenza-flusia';
import { useOnboarding, type TaskKey } from '../../lib/hooks/useOnboarding';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useUserData } from '../../lib/auth';
import { computeReconciledTasks } from '../../lib/onboarding/reconcileChecklist';
import { fetchOrgOnboardingSnapshot } from '../../lib/onboarding/fetchOrgSnapshot';
import { trackOnboardingEvent } from '../../lib/analytics/onboardingEvents';
import { WhatsAppSupportButton } from './support/WhatsAppSupportButton';
import { SetupChatPanel } from './SetupChatPanel';
import { supabase } from '../../lib/supabase';

/**
 * Setup guidato floating bottom-right con intake adattivo (LLM) +
 * checklist. Mounted UNA SOLA volta in Layout root.
 *
 * Stati visivi:
 *   - intake (3 step): solo per nuovi utenti, una volta. Skip o completo
 *     → checklist
 *   - intake loading: spinner mentre l'edge `onboarding-personalize`
 *     chiama Haiku via LiteLLM
 *   - checklist espansa
 *   - bubble (dismessa)
 *
 * Fallback: se l'LLM non risponde o l'edge function 5xx → passa
 * silenziosamente alla checklist con ordine default. Nessun errore mostrato
 * all'utente.
 */

interface TaskConfig {
  /** id univoco. Per primary coincide con TaskKey persistito in DB. */
  id: string;
  /** Solo primary: chiave nel JSONB onboarding_checklist.tasks */
  taskKey?: TaskKey;
  label: string;
  sub: string;
  href: string;
  tier: 'primary' | 'secondary';
  /** Permission resource (RBAC). Se assente, task visibile a tutti. */
  resource?: string;
  /** Permission action, default 'view' */
  action?: string;
  /** Quando true, task visibile SOLO a lead organizzativi (admin/manager).
   *  Usato per task di configurazione sistema (settings_*) dove tutti i ruoli
   *  hanno solo `view` ma solo l'admin dovrebbe configurarli realmente. */
  adminOnly?: boolean;
}

// Pattern copy: sub = motivazione (perche farlo), non descrizione campi.
// Resource/action mappati su MENU_CONFIG e tabella `permissions`.
const TASK_REGISTRY: TaskConfig[] = [
  // ─── PRIMARY: setup essenziale (10 task storici, persistiti in DB) ──────
  // Strategia filtering:
  // - resource + action 'create' → task azione operativa (es. crea lead).
  //   Visibile solo a utenti col permesso. User base senza leads:create non lo vede.
  // - adminOnly + resource 'view' → task config sistema. Visibile solo se
  //   isOrgLead (admin/manager). Tutti i ruoli hanno solo `view` su settings_*,
  //   ma solo admin/manager dovrebbero realmente configurare.
  // - nessun resource → sempre visibile (es. referral).
  { id: 'profile',      taskKey: 'profile',      tier: 'primary', adminOnly: true,  resource: 'settings_company',       action: 'view',   label: 'Completa il profilo azienda', sub: 'Lo vedono i clienti in fatture, preventivi ed email',         href: '/settings?tab=company' },
  { id: 'pipeline',     taskKey: 'pipeline',     tier: 'primary', adminOnly: true,  resource: 'settings_clients',       action: 'view',   label: 'Imposta la pipeline',         sub: 'Vedi a che punto sei con ogni cliente, niente dimenticati',   href: '/settings?tab=lead-pipelines' },
  { id: 'goal',         taskKey: 'goal',         tier: 'primary', adminOnly: true,  resource: 'settings_sales_targets', action: 'view',   label: 'Imposta obiettivo mensile',   sub: 'Vedi ogni giorno quanto ti manca per chiudere il mese',       href: '/settings?tab=sales-targets' },
  { id: 'integrations', taskKey: 'integrations', tier: 'primary', adminOnly: true,  resource: 'settings_api',           action: 'view',   label: 'Collega le integrazioni',     sub: 'I dati passano da soli — ore risparmiate ogni settimana',     href: '/settings?tab=integrations-hub' },
  { id: 'lead',         taskKey: 'lead',         tier: 'primary',                   resource: 'leads',                  action: 'create', label: 'Crea il tuo primo lead',      sub: 'Ogni cliente che paga e stato prima un lead — non perderli',  href: '/leads' },
  { id: 'deals',        taskKey: 'deals',        tier: 'primary',                   resource: 'deals',                  action: 'create', label: 'Crea la prima trattativa',    sub: 'Vedi quanto fatturato hai in lavorazione e cosa rischi',      href: '/deals' },
  { id: 'whatsapp',     taskKey: 'whatsapp',     tier: 'primary', adminOnly: true,  resource: 'settings_whatsapp',      action: 'view',   label: 'Collega WhatsApp',            sub: 'Tutte le chat dentro al CRM, anche un collega puo rispondere', href: '/settings?tab=whatsapp-config' },
  { id: 'automations',  taskKey: 'automations',  tier: 'primary', adminOnly: true,  resource: 'settings_automation',    action: 'view',   label: 'Automatizza un follow-up',    sub: 'Il lavoro ripetitivo lo fa Flusia — niente piu dimenticanze',  href: '/automation' },
  { id: 'team',         taskKey: 'team',         tier: 'primary',                   resource: 'users',                  action: 'create', label: 'Invita il team',              sub: 'Piu mani sui lead = piu trattative chiuse',                   href: '/settings?tab=users' },
  { id: 'referral',     taskKey: 'referral',     tier: 'primary',                                                                          label: "Invita un'azienda amica",     sub: 'Tu e lei un mese gratis ciascuno alla prima fattura',         href: '/referrals' },

  // ─── SECONDARY: "Scopri anche" (resto del CRM, non persistite in DB) ────
  { id: 'clients',         tier: 'secondary', label: 'Gestisci i clienti acquisiti', sub: 'Schede cliente, storia trattative, contatti',          href: '/clients',         resource: 'leads' },
  { id: 'contacts',        tier: 'secondary', label: 'Rubrica contatti',             sub: 'Tutti i contatti delle aziende in un posto solo',      href: '/contacts' },
  { id: 'projects',        tier: 'secondary', label: 'Avvia un progetto',            sub: 'Gestisci consegne post-vendita con timeline e task',   href: '/projects',        resource: 'projects' },
  { id: 'calendar',        tier: 'secondary', label: 'Calendario integrato',         sub: 'Riunioni, scadenze, follow-up sempre sotto controllo', href: '/calendar' },
  { id: 'meetings',        tier: 'secondary', label: 'Meetings con AI',              sub: 'Registra, trascrivi, estrai todo dai meeting',         href: '/meetings' },
  { id: 'tickets',         tier: 'secondary', label: 'Ticket di supporto',           sub: 'Centralizza le richieste post-vendita',                href: '/tickets' },
  { id: 'newsletter',      tier: 'secondary', label: 'Lancia una campagna email',    sub: 'Newsletter segmentate, tracking apri/click',           href: '/newsletter',      resource: 'newsletter' },
  { id: 'surveys',         tier: 'secondary', label: 'Crea un sondaggio',            sub: 'Raccogli feedback da clienti e prospect',              href: '/surveys' },
  { id: 'lead-forms',      tier: 'secondary', label: 'Form di acquisizione lead',    sub: 'Embed sul sito, lead entrano dritti in pipeline',      href: '/lead-forms' },
  { id: 'qr-codes',        tier: 'secondary', label: 'QR code tracciati',            sub: 'Misura volantini, fiere, packaging fisico',            href: '/qr-codes',        resource: 'qr_codes' },
  { id: 'bookings',        tier: 'secondary', label: 'Prenotazioni online',          sub: 'I clienti si prenotano da soli, fine al ping-pong',    href: '/bookings',        resource: 'booking' },
  { id: 'fattureincloud',  tier: 'secondary', label: 'Fatture in Cloud',             sub: 'Emetti, sincronizza, marginalita sotto controllo',     href: '/fattureincloud' },
  { id: 'commissions',     tier: 'secondary', label: 'Provvigioni team',             sub: 'Calcola in automatico chi prende cosa',                href: '/commissions' },
  { id: 'services',        tier: 'secondary', label: 'Catalogo servizi',             sub: 'Listini, varianti, costi standard pronti all’uso',     href: '/services' },
  { id: 'inventory',       tier: 'secondary', label: 'Magazzino prodotti',           sub: 'Scorte, movimenti, alert sotto soglia',                href: '/magazzino' },
  { id: 'ads',             tier: 'secondary', label: 'Meta Ads attribution',         sub: 'Sai quale campagna porta i lead che convertono',       href: '/ads',             resource: 'marketing' },
  { id: 'gmail',           tier: 'secondary', label: 'Gmail nel CRM',                sub: 'Email viste accanto alla scheda cliente',              href: '/gmail' },
  { id: 'recordings',      tier: 'secondary', label: 'Registra schermo',             sub: 'Video tutorial e demo da inviare al volo',             href: '/recordings',      resource: 'screen_recordings' },
  { id: 'wiki',            tier: 'secondary', label: 'Wiki aziendale',               sub: 'Procedure, onboarding nuovi colleghi, knowledge base', href: '/wiki' },
];

const VIRTUAL_FIRST_TASK = {
  label: 'Registrazione su Flusia',
  sub: 'Sei dentro — il primo passo e gia fatto',
};

const EXPANDED_INNER_STORAGE_KEY = 'flusia-setup-checklist-inner-expanded';
const INTAKE_STATUS_KEY = 'flusia-intake-status';
const RECONCILE_SESSION_KEY = 'flusia-onboarding-reconciled-v1';

/** Task che la riconciliazione può spuntare guardando i dati reali dell'org
 *  (referral escluso: resta solo event-driven). */
const RECONCILABLE_TASKS: TaskKey[] = [
  'profile', 'pipeline', 'goal', 'integrations', 'lead',
  'deals', 'whatsapp', 'automations', 'team',
];

type IntakeStatus = 'pending' | 'completed' | 'skipped' | null;
type IntakeStep = 'from' | 'goal' | 'open' | 'loading';

interface FromChoice { key: string; label: string; }
interface GoalChoice { key: string; label: string; }

const FROM_CHOICES: FromChoice[] = [
  { key: 'other-crm',     label: 'Da un altro CRM (Pipedrive, HubSpot...)' },
  { key: 'spreadsheet',   label: 'Da Excel o Google Sheet' },
  { key: 'memory',        label: 'Da niente, gestisco a mente' },
  { key: 'flusia-trial',  label: 'Da Flusia (ho gia provato il trial)' },
];

const GOAL_CHOICES: GoalChoice[] = [
  { key: 'close-deals',   label: 'Chiudere piu trattative' },
  { key: 'organize-leads', label: 'Mettere ordine nei lead' },
  { key: 'team-coord',    label: 'Coordinare il team' },
  { key: 'automate',      label: 'Automatizzare il lavoro ripetitivo' },
];

interface LlmPersonalization {
  welcome: string;
  priority_order: TaskKey[];
  highlight_first: TaskKey;
}

function readIntakeStatus(): IntakeStatus {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(INTAKE_STATUS_KEY);
    if (v === 'pending' || v === 'completed' || v === 'skipped') return v;
    return null;
  } catch {
    return null;
  }
}

function writeIntakeStatus(s: IntakeStatus): void {
  if (typeof window === 'undefined') return;
  try {
    if (s === null) window.localStorage.removeItem(INTAKE_STATUS_KEY);
    else window.localStorage.setItem(INTAKE_STATUS_KEY, s);
  } catch {
    // ignore
  }
}

// Su localhost mostriamo un piccolo bottone reset (dev only).
const IS_DEV = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

export function SetupFloatingBubble() {
  const navigate = useNavigate();
  const { checklist, state, loading: onboardingLoading, markTaskComplete, markTasksComplete, dismissChecklist, resetOnboarding, refetch } = useOnboarding();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const { organizationId } = useUserData();
  // Rispetta prefers-reduced-motion del sistema utente: skip animazioni
  // di scale/spring/easing della card. Lascia comunque gli stati visivi.
  const prefersReducedMotion = useReducedMotion();

  // Distinzione "lead organizzativo" vs "utente base".
  // Solo chi puo invitare altri utenti (admin/manager) vede:
  // - Intake adattivo LLM (personalizzazione strategy → richiede prospettiva business)
  // - Tab "Flusia AI" (chat sull'onboarding e configurazione CRM)
  // Gli utenti base vedono solo la checklist filtrata dai loro permessi.
  const isOrgLead = !permissionsLoading && hasPermission('users', 'create');

  // Stato secondario "Scopri anche": collassato di default, auto-expand quando
  // tutti i primary sono done (segnale "ok, ora puoi esplorare il resto")
  const [discoverExpanded, setDiscoverExpanded] = useState(false);

  // Riapertura dal bottone in sidebar (Layout): l'evento arriva DOPO che il
  // restore e' persistito su DB → refetch per far ricomparire subito la card
  // (il realtime su user_profiles non e' abbastanza affidabile per il live update).
  useEffect(() => {
    const onRestored = () => { void refetch(); };
    window.addEventListener('onboarding:checklist-restored', onRestored);
    return () => window.removeEventListener('onboarding:checklist-restored', onRestored);
  }, [refetch]);

  // Tour driver.js attivo: nascondi la bubble per evitare overlap col popover
  const [tourActive, setTourActive] = useState(false);
  useEffect(() => {
    const onStart = () => setTourActive(true);
    const onEnd = () => setTourActive(false);
    window.addEventListener('flusia:tour-started', onStart);
    window.addEventListener('flusia:tour-ended', onEnd);
    return () => {
      window.removeEventListener('flusia:tour-started', onStart);
      window.removeEventListener('flusia:tour-ended', onEnd);
    };
  }, []);

  // Tab attivo nella card: 'setup' (checklist) | 'chat' (Flusia AI)
  const [activeTab, setActiveTab] = useState<'setup' | 'chat'>(() => {
    if (typeof window === 'undefined') return 'setup';
    try {
      const saved = window.localStorage.getItem('flusia-setup-active-tab');
      return saved === 'chat' ? 'chat' : 'setup';
    } catch {
      return 'setup';
    }
  });
  const switchTab = useCallback((tab: 'setup' | 'chat') => {
    setActiveTab(tab);
    try { window.localStorage.setItem('flusia-setup-active-tab', tab); } catch { /* ignore */ }
    if (tab === 'chat') trackOnboardingEvent('flusia.onboarding.chat_opened', {});
  }, []);

  // ─── Card inner expand ────────────────────────────────────────────
  const [innerExpanded, setInnerExpandedRaw] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      const v = window.localStorage.getItem(EXPANDED_INNER_STORAGE_KEY);
      return v === null ? true : v === 'true';
    } catch {
      return true;
    }
  });
  const setInnerExpanded = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
    setInnerExpandedRaw((prev) => {
      const val = typeof next === 'function' ? next(prev) : next;
      try {
        window.localStorage.setItem(EXPANDED_INNER_STORAGE_KEY, val ? 'true' : 'false');
      } catch {
        // ignore
      }
      return val;
    });
  }, []);

  // ─── Intake state ─────────────────────────────────────────────────
  // Lo stato iniziale e null finche non abbiamo letto state da DB: evita
  // un flash di intake per utenti che lo hanno gia completato server-side.
  const [intakeStatus, setIntakeStatus] = useState<IntakeStatus>(() => {
    const stored = readIntakeStatus();
    return stored === 'completed' || stored === 'skipped' ? stored : null;
  });
  const [intakeResolved, setIntakeResolved] = useState<boolean>(() => {
    const stored = readIntakeStatus();
    return stored === 'completed' || stored === 'skipped';
  });
  const [intakeStep, setIntakeStep] = useState<IntakeStep>('from');
  const [intakeFrom, setIntakeFrom] = useState<string | null>(null);
  const [intakeGoal, setIntakeGoal] = useState<string | null>(null);
  const [intakeOpen, setIntakeOpen] = useState('');
  const [llmResult, setLlmResult] = useState<LlmPersonalization | null>(null);

  // Quando il backend ha caricato lo state, decidi lo status definitivo.
  // - se DB ha intake → completed (e sincronizza localStorage + recupera llm_response)
  // - se localStorage e completed/skipped → quello vince
  // - altrimenti → pending (mostra intake anche per utenti legacy senza record)
  //
  // Multi-tab re-sync: anche dopo intakeResolved=true, se arriva un intake dal DB
  // (es. l'utente lo ha completato in un'altra tab) sincronizziamo llmResult e
  // status. Non re-degradiamo mai pending → completed se gia risolto in locale.
  useEffect(() => {
    const intakeRecord = (state as { intake?: Record<string, unknown> } | null)?.intake;

    if (!intakeResolved) {
      if (intakeRecord) {
        setIntakeStatus('completed');
        writeIntakeStatus('completed');
        const llm = (intakeRecord as { llm_response?: LlmPersonalization | null }).llm_response;
        if (llm) setLlmResult(llm);
        setIntakeResolved(true);
        return;
      }
      // Se state ha valore (non null) ma niente intake field → mai fatto → pending
      if (state !== null) {
        const stored = readIntakeStatus();
        if (stored === 'completed' || stored === 'skipped') {
          setIntakeStatus(stored);
        } else {
          setIntakeStatus('pending');
          writeIntakeStatus('pending');
        }
        setIntakeResolved(true);
      }
      // Se state e null e localStorage non ha info → resto in attesa (null status)
      return;
    }

    // intakeResolved = true ma arriva intake da realtime (altra tab) → sync.
    // Promuoviamo 'pending' → 'completed' ma non sovrascriviamo 'skipped' del locale
    // (rispetta scelta utente in questa tab).
    if (intakeRecord && intakeStatus !== 'completed' && intakeStatus !== 'skipped') {
      setIntakeStatus('completed');
      writeIntakeStatus('completed');
    }
    if (intakeRecord && !llmResult) {
      const llm = (intakeRecord as { llm_response?: LlmPersonalization | null }).llm_response;
      if (llm) setLlmResult(llm);
    }
  }, [state, intakeResolved, intakeStatus, llmResult]);

  // Edge case: utenti totalmente nuovi (no row user_profiles?). Dopo 2s senza
  // segnali concreti, considera "pending" per evitare di restare bloccati su null.
  useEffect(() => {
    if (intakeResolved) return;
    const t = setTimeout(() => {
      if (!intakeResolved) {
        const stored = readIntakeStatus();
        setIntakeStatus(stored === 'completed' || stored === 'skipped' ? stored : 'pending');
        if (!stored) writeIntakeStatus('pending');
        setIntakeResolved(true);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [intakeResolved]);

  // Solo lead organizzativi vedono l'intake (decisioni di setup strategiche).
  // Gli utenti base vanno diretti alla checklist filtrata.
  const isIntakeMode = intakeStatus === 'pending' && isOrgLead;

  // ─── Event bus listener ───────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ taskId: TaskKey }>).detail;
      if (detail?.taskId) {
        void markTaskComplete(detail.taskId);
        trackOnboardingEvent('flusia.onboarding.checklist_task_completed', {
          task: detail.taskId,
          via: 'event_bus',
        });
      }
    };
    window.addEventListener('onboarding:task-completed', handler);
    return () => window.removeEventListener('onboarding:task-completed', handler);
  }, [markTaskComplete]);

  useEffect(() => {
    const handler = () => setInnerExpanded(true);
    window.addEventListener('onboarding:checklist-restored', handler);
    return () => window.removeEventListener('onboarding:checklist-restored', handler);
  }, [setInnerExpanded]);

  // ─── Riconciliazione con lo stato reale dell'org ──────────────────
  // I task si spuntano solo via eventi client-side: chi ha configurato il CRM
  // da percorsi senza dispatch (import, API, o prima che i dispatch esistessero)
  // resterebbe con la checklist ferma per sempre. Una volta per sessione
  // confrontiamo i dati reali e sanamo i task già di fatto completati.
  useEffect(() => {
    if (onboardingLoading || !intakeResolved || !organizationId) return;
    try {
      if (sessionStorage.getItem(RECONCILE_SESSION_KEY) === '1') return;
    } catch {
      // sessionStorage non disponibile (private mode) → procedi comunque
    }
    const hasCandidate = RECONCILABLE_TASKS.some(
      (t) => checklist?.tasks?.[t]?.done !== true,
    );
    if (!hasCandidate) return;
    try {
      sessionStorage.setItem(RECONCILE_SESSION_KEY, '1');
    } catch {
      // ignore
    }
    let cancelled = false;
    void (async () => {
      try {
        const snapshot = await fetchOrgOnboardingSnapshot(organizationId);
        if (cancelled) return;
        const toMark = computeReconciledTasks(snapshot, checklist);
        if (toMark.length === 0) return;
        await markTasksComplete(toMark);
        trackOnboardingEvent('flusia.onboarding.checklist_reconciled', {
          tasks: toMark.join(','),
        });
      } catch {
        // Fail-safe: la checklist resta com'è, si ritenta alla prossima sessione
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onboardingLoading, intakeResolved, organizationId, checklist, markTasksComplete]);

  // ─── Permission filtering + tier separation ─────────────────────
  const tasksMap = (checklist?.tasks ?? {}) as Record<TaskKey, { done?: boolean } | undefined>;

  // Filter logic:
  // - loading → mostra tutto (no flash di task scomparsi/apparsi)
  // - adminOnly task → solo se isOrgLead
  // - resource + action → check permission specifico
  const isTaskVisible = useCallback((t: TaskConfig): boolean => {
    if (permissionsLoading) return true;
    if (t.adminOnly && !isOrgLead) return false;
    if (t.resource) return hasPermission(t.resource, t.action ?? 'view');
    return true;
  }, [hasPermission, permissionsLoading, isOrgLead]);

  const visiblePrimary = useMemo<TaskConfig[]>(() => {
    return TASK_REGISTRY.filter((t) => t.tier === 'primary' && isTaskVisible(t));
  }, [isTaskVisible]);

  const visibleSecondary = useMemo<TaskConfig[]>(() => {
    return TASK_REGISTRY.filter((t) => t.tier === 'secondary' && isTaskVisible(t));
  }, [isTaskVisible]);

  // Riordino dei primary se LLM ha risposto (rispettando il filtro permessi)
  const orderedPrimary = useMemo<TaskConfig[]>(() => {
    if (!llmResult) return visiblePrimary;
    const map = new Map(visiblePrimary.map((t) => [t.id, t]));
    const ordered: TaskConfig[] = [];
    for (const id of llmResult.priority_order) {
      const t = map.get(id);
      if (t) {
        ordered.push(t);
        map.delete(id);
      }
    }
    for (const t of map.values()) ordered.push(t);
    return ordered;
  }, [visiblePrimary, llmResult]);

  // ─── Counter (basato su primary visibili) ───────────────────────
  const realDone = visiblePrimary.filter((t) => t.taskKey && tasksMap[t.taskKey]?.done).length;
  const doneCount = realDone + 1; // +1 task virtuale "Registrazione su Flusia"
  const totalCount = visiblePrimary.length + 1;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 100;
  const primaryAllDone = totalCount > 1 && realDone === visiblePrimary.length;

  // Auto-expand "Scopri anche" la prima volta che tutti i primary sono done
  useEffect(() => {
    if (primaryAllDone && !discoverExpanded) {
      setDiscoverExpanded(true);
      trackOnboardingEvent('flusia.onboarding.discover_unlocked', {});
    }
  }, [primaryAllDone, discoverExpanded]);

  // ─── Handlers ─────────────────────────────────────────────────────
  const handleTaskClick = useCallback(
    (task: TaskConfig) => {
      // Primary done? skip click
      if (task.taskKey) {
        const isDone = tasksMap[task.taskKey]?.done ?? false;
        if (isDone) return;
      }
      // Per i primary aggiungo ?tour=<taskKey> per triggerare driver.js.
      // Per i secondary navigo soltanto (no tour disponibile).
      if (task.taskKey) {
        const separator = task.href.includes('?') ? '&' : '?';
        navigate(`${task.href}${separator}tour=${encodeURIComponent(task.taskKey)}`);
      } else {
        navigate(task.href);
        trackOnboardingEvent('flusia.onboarding.discover_clicked', { task_id: task.id });
      }
    },
    [tasksMap, navigate],
  );

  const handleDismiss = useCallback(async () => {
    await dismissChecklist();
    // Notifica il Layout DOPO la persistenza: al refetch trova dismissed=true
    // e mostra subito l'iconcina "Riapri setup guidato" in fondo alla sidebar.
    window.dispatchEvent(new CustomEvent('onboarding:checklist-dismissed'));
  }, [dismissChecklist]);

  // Dev-only reset (visibile solo localhost) — pulisce localStorage + DB
  const handleDevReset = useCallback(async () => {
    if (!IS_DEV) return;
    if (!window.confirm('Reset completo onboarding (localStorage + DB)?')) return;
    try {
      const keys = [
        'flusia-intake-status',
        'flusia-setup-checklist-expanded',
        'flusia-setup-checklist-first-touch',
        'flusia-setup-checklist-inner-expanded',
        'flusia-onboarding-welcome-dismissed',
        'flusia-setup-active-tab',
      ];
      keys.forEach((k) => localStorage.removeItem(k));
      Object.keys(localStorage)
        .filter((k) =>
          k.startsWith('flusia-tour-seen-') ||
          k.startsWith('flusia-tour-fired-') ||
          k.startsWith('flusia-onboarding-autoprogress-'),
        )
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }
    try {
      await resetOnboarding();
    } catch {
      // ignore
    }
    window.location.reload();
  }, [resetOnboarding]);

  const handleSkipIntake = useCallback(() => {
    setIntakeStatus('skipped');
    writeIntakeStatus('skipped');
    trackOnboardingEvent('flusia.onboarding.intake_skipped', {
      at_step: intakeStep,
    });
  }, [intakeStep]);

  const submitIntake = useCallback(async () => {
    setIntakeStep('loading');
    trackOnboardingEvent('flusia.onboarding.intake_llm_started', {
      from: intakeFrom ?? '',
      goal: intakeGoal ?? '',
    });

    // Timeout di sicurezza: 12s, poi fallback silenzioso a checklist standard.
    const timeoutMs = 12_000;
    let didFinish = false;
    const fallbackTimer = setTimeout(() => {
      if (didFinish) return;
      didFinish = true;
      setIntakeStatus('completed');
      writeIntakeStatus('completed');
      trackOnboardingEvent('flusia.onboarding.intake_llm_timeout', {});
    }, timeoutMs);

    try {
      const { data, error } = await supabase.functions.invoke('onboarding-personalize', {
        body: {
          from: intakeFrom,
          goal: intakeGoal,
          openAnswer: intakeOpen.trim(),
        },
      });

      if (didFinish) return; // timeout gia scattato
      didFinish = true;
      clearTimeout(fallbackTimer);

      if (error || !data) {
        // Fallback silenzioso
        setIntakeStatus('completed');
        writeIntakeStatus('completed');
        return;
      }

      if (data.personalized && data.welcome && Array.isArray(data.priority_order)) {
        setLlmResult({
          welcome: data.welcome,
          priority_order: data.priority_order,
          highlight_first: data.highlight_first,
        });
        trackOnboardingEvent('flusia.onboarding.intake_llm_success', {});
      } else {
        trackOnboardingEvent('flusia.onboarding.intake_llm_unavailable', {
          reason: typeof data.reason === 'string' ? data.reason : 'unknown',
        });
      }
      setIntakeStatus('completed');
      writeIntakeStatus('completed');
    } catch (err) {
      if (!didFinish) {
        didFinish = true;
        clearTimeout(fallbackTimer);
        setIntakeStatus('completed');
        writeIntakeStatus('completed');
        trackOnboardingEvent('flusia.onboarding.intake_llm_failed', {
          message: err instanceof Error ? err.message : 'unknown',
        });
      }
    }
  }, [intakeFrom, intakeGoal, intakeOpen]);

  if (typeof document === 'undefined') return null;

  // Gate anti-flash: non mostrare nulla finche lo stato non e caricato dal DB e
  // l'intake non e risolto. Senza questo, `checklist` parte null → la card si
  // apre espansa e poi collassa nel tondino quando arriva `dismissed=true`.
  // Cosi la PRIMA cosa visibile e gia lo stato definitivo (tondino o card).
  if (onboardingLoading || !intakeResolved) return null;

  const isDismissed = !!checklist?.dismissed;

  // Checklist chiusa: niente elementi flottanti (il tondino dava fastidio).
  // La riapertura passa SOLO dal bottone "Riapri setup guidato" in fondo alla sidebar.
  if (isDismissed) return null;

  return createPortal(
    <div
      className="fixed bottom-5 right-5 z-20 pointer-events-none transition-opacity duration-200"
      style={{
        opacity: tourActive ? 0 : 1,
        visibility: tourActive ? 'hidden' : 'visible',
        // Safe-area bottom iOS + protezione su tastiera virtuale aperta
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {(
          <motion.div
            key="card"
            initial={prefersReducedMotion ? { opacity: 0 } : { scale: 0.6, opacity: 0, y: 30 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { scale: 1, opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { scale: 0.6, opacity: 0, y: 30 }}
            transition={prefersReducedMotion ? { duration: 0.1 } : { type: 'spring', stiffness: 320, damping: 26 }}
            style={{
              transformOrigin: 'bottom right',
              boxShadow:
                '0 12px 28px -8px rgba(15, 23, 42, 0.18), 0 4px 12px -4px rgba(233, 30, 140, 0.18), 0 0 0 1px rgba(55, 53, 47, 0.08)',
              // Sfondo Flusia: gradient sfumato in cima che svanisce verso bianco.
              // Top: rosa-arancio al ~10% opacita; verso il fondo: bianco solido.
              backgroundImage:
                'linear-gradient(180deg, rgba(233, 30, 140, 0.10) 0%, rgba(249, 115, 22, 0.06) 20%, rgba(255, 255, 255, 0.0) 50%)',
              backgroundColor: 'white',
            }}
            className="pointer-events-auto w-[320px] max-w-[calc(100vw-2.5rem)] rounded-lg dark:bg-[#202022] overflow-hidden relative"
          >
            {/* Ribbon gradient 3px in cima — accento Flusia visibile */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-[3px] pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, #E91E8C, #F97316)',
              }}
            />
            {isIntakeMode ? (
              <IntakeView
                step={intakeStep}
                from={intakeFrom}
                goal={intakeGoal}
                openAnswer={intakeOpen}
                onSelectFrom={(k) => { setIntakeFrom(k); setIntakeStep('goal'); }}
                onSelectGoal={(k) => { setIntakeGoal(k); setIntakeStep('open'); }}
                onChangeOpen={setIntakeOpen}
                onBack={() => {
                  if (intakeStep === 'goal') setIntakeStep('from');
                  else if (intakeStep === 'open') setIntakeStep('goal');
                }}
                onSubmit={submitIntake}
                onSkip={handleSkipIntake}
              />
            ) : (
              <>
                {/* Tab bar: Setup | Flusia AI (chat solo per lead organizzativi) */}
                {isOrgLead && (
                  <div
                    role="tablist"
                    aria-label="Setup guidato e assistente Flusia AI"
                    className="flex border-b border-[#EBEBEA] dark:border-white/10 px-2 pt-2 gap-1"
                  >
                    <TabButton
                      active={activeTab === 'setup'}
                      onClick={() => switchTab('setup')}
                      icon={<ListChecks className="w-3.5 h-3.5" strokeWidth={2} />}
                      label="Setup"
                    />
                    <TabButton
                      active={activeTab === 'chat'}
                      onClick={() => switchTab('chat')}
                      icon={<MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />}
                      label="Flusia AI"
                    />
                  </div>
                )}

                {(isOrgLead && activeTab === 'chat') ? (
                  <SetupChatPanel />
                ) : (
                  <ChecklistView
                    primaryTasks={orderedPrimary}
                    secondaryTasks={visibleSecondary}
                    tasksMap={tasksMap}
                    doneCount={doneCount}
                    totalCount={totalCount}
                    pct={pct}
                    primaryAllDone={primaryAllDone}
                    discoverExpanded={discoverExpanded}
                    setDiscoverExpanded={setDiscoverExpanded}
                    innerExpanded={innerExpanded}
                    setInnerExpanded={setInnerExpanded}
                    handleTaskClick={handleTaskClick}
                    handleDismiss={handleDismiss}
                    welcomeMessage={llmResult?.welcome ?? null}
                    highlightFirst={llmResult?.highlight_first ?? null}
                    onDevReset={IS_DEV ? handleDevReset : null}
                  />
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
}

// ─── Sub-component: intake (3 step + loading) ────────────────────────
interface IntakeViewProps {
  step: IntakeStep;
  from: string | null;
  goal: string | null;
  openAnswer: string;
  onSelectFrom: (k: string) => void;
  onSelectGoal: (k: string) => void;
  onChangeOpen: (v: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  onSkip: () => void;
}
function IntakeView(props: IntakeViewProps) {
  const { step, from, goal, openAnswer, onSelectFrom, onSelectGoal, onChangeOpen, onBack, onSubmit, onSkip } = props;
  const stepNum = step === 'from' ? 1 : step === 'goal' ? 2 : 3;
  const prefersReducedMotion = useReducedMotion();
  // Animazioni step-transition disabilitate quando reduced-motion: fade-only veloce.
  const stepMotion = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 } }
    : { initial: { opacity: 0, x: 12 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -12 }, transition: { duration: 0.2 } };

  return (
    <div className="p-3.5">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-pink-500" strokeWidth={2} />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#787774] dark:text-white/50">
            Personalizziamo · {step === 'loading' ? '...' : `${stepNum}/3`}
          </span>
        </div>
        {step !== 'loading' && (
          <button
            type="button"
            onClick={onSkip}
            className="text-[11px] text-[#9B9A97] hover:text-[#37352F] dark:hover:text-white transition-colors"
          >
            Salta
          </button>
        )}
      </div>

      {/* Mini progress dots */}
      {step !== 'loading' && (
        <div className="flex gap-1 mb-3">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className={`h-1 flex-1 rounded-full transition-colors ${
                n <= stepNum
                  ? 'bg-gradient-to-r from-[#E91E8C] to-[#F97316]'
                  : 'bg-[#EBEBEA] dark:bg-white/10'
              }`}
            />
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 'from' && (
          <motion.div
            key="from"
            initial={stepMotion.initial}
            animate={stepMotion.animate}
            exit={stepMotion.exit}
            transition={stepMotion.transition}
          >
            <h3 className="text-[14px] font-semibold text-[#37352F] dark:text-white mb-2.5 leading-tight">
              Da dove arrivi?
            </h3>
            <div className="space-y-1.5">
              {FROM_CHOICES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => onSelectFrom(c.key)}
                  className={`w-full text-left px-3 py-2 rounded-md text-[12.5px] border transition-all ${
                    from === c.key
                      ? 'border-pink-500 bg-pink-50/60 dark:bg-pink-900/15 text-[#37352F] dark:text-white'
                      : 'border-[#EBEBEA] dark:border-white/10 text-[#37352F] dark:text-white/85 hover:border-pink-300 hover:bg-[#FDF2F8] dark:hover:bg-pink-900/10'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {step === 'goal' && (
          <motion.div
            key="goal"
            initial={stepMotion.initial}
            animate={stepMotion.animate}
            exit={stepMotion.exit}
            transition={stepMotion.transition}
          >
            <h3 className="text-[14px] font-semibold text-[#37352F] dark:text-white mb-2.5 leading-tight">
              Cosa vuoi sistemare per primo?
            </h3>
            <div className="space-y-1.5">
              {GOAL_CHOICES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => onSelectGoal(c.key)}
                  className={`w-full text-left px-3 py-2 rounded-md text-[12.5px] border transition-all ${
                    goal === c.key
                      ? 'border-pink-500 bg-pink-50/60 dark:bg-pink-900/15 text-[#37352F] dark:text-white'
                      : 'border-[#EBEBEA] dark:border-white/10 text-[#37352F] dark:text-white/85 hover:border-pink-300 hover:bg-[#FDF2F8] dark:hover:bg-pink-900/10'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex justify-start">
              <button
                type="button"
                onClick={onBack}
                className="text-[11.5px] text-[#787774] hover:text-[#37352F] dark:hover:text-white inline-flex items-center gap-1 transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Indietro
              </button>
            </div>
          </motion.div>
        )}

        {step === 'open' && (
          <motion.div
            key="open"
            initial={stepMotion.initial}
            animate={stepMotion.animate}
            exit={stepMotion.exit}
            transition={stepMotion.transition}
          >
            <h3 className="text-[14px] font-semibold text-[#37352F] dark:text-white mb-1 leading-tight">
              Raccontami in 2 righe
            </h3>
            <p className="text-[11.5px] text-[#787774] dark:text-white/50 mb-2.5">
              Cosa vendi e come acquisisci clienti? Cosi posso ordinare il setup su misura.
            </p>
            <textarea
              value={openAnswer}
              onChange={(e) => onChangeOpen(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder='Es: "Vendo siti web a piccole aziende. I lead arrivano da Meta Ads e passaparola."'
              aria-label="Cosa vendi e come acquisisci clienti"
              className="w-full px-2.5 py-2 text-[12.5px] rounded-md border border-[#EBEBEA] dark:border-white/15 bg-white dark:bg-[#202022] text-[#37352F] dark:text-white placeholder:text-[#9B9A97] dark:placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400 resize-none"
            />
            <div className="mt-3 flex justify-between items-center">
              <button
                type="button"
                onClick={onBack}
                className="text-[11.5px] text-[#787774] hover:text-[#37352F] dark:hover:text-white inline-flex items-center gap-1 transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Indietro
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!openAnswer.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-white bg-gradient-to-r from-[#E91E8C] to-[#F97316] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
              >
                Personalizza
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}

        {step === 'loading' && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-6 text-center"
          >
            <div className="relative inline-flex items-center justify-center w-10 h-10 mb-3">
              <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316] opacity-30 animate-ping" />
              <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316]">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
              </span>
            </div>
            <p className="text-[12.5px] text-[#37352F] dark:text-white font-medium">
              Sto preparando il tuo setup...
            </p>
            <p className="text-[11px] text-[#787774] dark:text-white/50 mt-1">
              Ci vuole qualche secondo
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Sub-component: checklist ────────────────────────────────────────
interface ChecklistViewProps {
  primaryTasks: TaskConfig[];
  secondaryTasks: TaskConfig[];
  tasksMap: Record<TaskKey, { done?: boolean } | undefined>;
  doneCount: number;
  totalCount: number;
  pct: number;
  primaryAllDone: boolean;
  discoverExpanded: boolean;
  setDiscoverExpanded: (v: boolean | ((p: boolean) => boolean)) => void;
  innerExpanded: boolean;
  setInnerExpanded: (next: boolean | ((p: boolean) => boolean)) => void;
  handleTaskClick: (t: TaskConfig) => void;
  handleDismiss: () => void | Promise<void>;
  welcomeMessage: string | null;
  highlightFirst: TaskKey | null;
  onDevReset: (() => void | Promise<void>) | null;
}
function ChecklistView(props: ChecklistViewProps) {
  const {
    primaryTasks, secondaryTasks, tasksMap,
    doneCount, totalCount, pct,
    primaryAllDone, discoverExpanded, setDiscoverExpanded,
    innerExpanded, setInnerExpanded,
    handleTaskClick, handleDismiss,
    welcomeMessage, highlightFirst,
    onDevReset,
  } = props;
  return (
    <>
      <button
        type="button"
        onClick={() => setInnerExpanded((e) => !e)}
        className="w-full px-3.5 py-2.5 flex items-center gap-2.5 text-left hover:bg-[#FAFAFA] dark:hover:bg-white/5 transition-colors rounded-t-lg"
        aria-label={innerExpanded ? 'Comprimi setup' : 'Espandi setup'}
      >
        <span
          className="shrink-0 w-2 h-2 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316]"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-[#37352F] dark:text-white truncate">
            Setup guidato
          </div>
          <div
            className="text-[11.5px] mt-0.5"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent font-semibold tabular-nums">
              {doneCount}
            </span>
            <span className="text-[#787774] dark:text-white/50"> di {totalCount} completati</span>
          </div>
        </div>
        <ChevronDown
          className={`shrink-0 w-3.5 h-3.5 transition-transform ${innerExpanded ? 'rotate-180 text-pink-500' : 'text-[#9B9A97]'}`}
          strokeWidth={2}
        />
      </button>

      <div className="px-3.5 pb-2.5">
        <div
          className="h-1 rounded-full bg-[#EBEBEA] dark:bg-white/10 overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progresso setup ${pct}%`}
        >
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#E91E8C] to-[#F97316]"
            initial={false}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {innerExpanded && (
          <motion.div
            key="card-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="border-t border-[#EBEBEA] dark:border-white/10 overflow-hidden"
          >
            {welcomeMessage && (
              <div className="mx-2 mt-2 mb-1 px-2.5 py-2 rounded-md bg-gradient-to-br from-pink-50 to-orange-50 dark:from-pink-900/15 dark:to-orange-900/10">
                <div className="flex items-start gap-1.5">
                  <Sparkles className="w-3 h-3 text-pink-500 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <p className="text-[11.5px] leading-snug text-[#37352F] dark:text-white/85">
                    {welcomeMessage}
                  </p>
                </div>
              </div>
            )}

            <div className="pt-1.5 pb-1 max-h-[55vh] overflow-y-auto">
              {/* ─── Setup guidato (primary) ─── */}
              <div className="px-1.5 space-y-px">
                {/* Task virtuale Registrazione */}
                <div className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md">
                  <NotionCheckbox done />
                  <span className="flex-1 min-w-0">
                    <div className="text-[12.5px] leading-tight line-through text-[#9B9A97] dark:text-white/30">
                      {VIRTUAL_FIRST_TASK.label}
                    </div>
                  </span>
                </div>

                {primaryTasks.map((task) => {
                  const isDone = task.taskKey ? tasksMap[task.taskKey]?.done ?? false : false;
                  const isHighlight = highlightFirst === task.taskKey && !isDone;
                  return (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => handleTaskClick(task)}
                      disabled={isDone}
                      className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors group ${
                        isDone
                          ? 'cursor-default'
                          : isHighlight
                            ? 'bg-gradient-to-r from-pink-50 via-orange-50/60 to-transparent dark:from-pink-900/15 hover:from-pink-100/80 dark:hover:from-pink-900/20'
                            : 'hover:bg-[#FDF2F8] dark:hover:bg-pink-900/10'
                      }`}
                    >
                      <NotionCheckbox done={isDone} />
                      <span className="flex-1 min-w-0">
                        <div
                          className={`text-[12.5px] leading-tight ${
                            isDone
                              ? 'line-through text-[#9B9A97] dark:text-white/30'
                              : isHighlight
                                ? 'font-semibold text-pink-700 dark:text-pink-300'
                                : 'text-[#37352F] dark:text-white/90 group-hover:text-pink-700 dark:group-hover:text-pink-300 transition-colors'
                          }`}
                        >
                          {task.label}
                        </div>
                        {!isDone && (
                          <div className="text-[11px] text-[#9B9A97] dark:text-white/40 mt-0.5 leading-tight truncate">
                            {task.sub}
                          </div>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* ─── Scopri anche (secondary) ─── */}
              {secondaryTasks.length > 0 && (
                <div className="mt-2.5 pt-2 px-1.5 border-t border-[#EBEBEA] dark:border-white/10">
                  <button
                    type="button"
                    onClick={() => setDiscoverExpanded((v) => !v)}
                    className="w-full flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[#FAFAFA] dark:hover:bg-white/5 transition-colors group"
                  >
                    <Compass className="w-3 h-3 text-[#787774] dark:text-white/50 group-hover:text-pink-500" strokeWidth={2} />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#787774] dark:text-white/50 group-hover:text-[#37352F] dark:group-hover:text-white transition-colors">
                      Scopri anche
                    </span>
                    <span className="text-[11px] text-[#9B9A97] dark:text-white/30">
                      · {secondaryTasks.length}
                    </span>
                    {primaryAllDone && !discoverExpanded && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316]"
                        aria-hidden="true"
                        title="Hai sbloccato la sezione completa"
                      />
                    )}
                    <ChevronDown
                      className={`ml-auto w-3 h-3 transition-transform ${discoverExpanded ? 'rotate-180 text-pink-500' : 'text-[#9B9A97]'}`}
                      strokeWidth={2}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {discoverExpanded && (
                      <motion.div
                        key="discover-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-px mt-1">
                          {secondaryTasks.map((task) => (
                            <button
                              key={task.id}
                              type="button"
                              onClick={() => handleTaskClick(task)}
                              className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left hover:bg-[#FDF2F8] dark:hover:bg-pink-900/10 transition-colors group"
                            >
                              <span
                                className="shrink-0 w-[16px] h-[16px] rounded-full border border-[#D4D3CF] dark:border-white/20 inline-flex items-center justify-center transition-all group-hover:border-pink-400"
                                aria-hidden="true"
                              />
                              <span className="flex-1 min-w-0">
                                <div className="text-[12.5px] leading-tight text-[#37352F] dark:text-white/90 group-hover:text-pink-700 dark:group-hover:text-pink-300 transition-colors">
                                  {task.label}
                                </div>
                                <div className="text-[11px] text-[#9B9A97] dark:text-white/40 mt-0.5 leading-tight truncate">
                                  {task.sub}
                                </div>
                              </span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {!primaryAllDone && (
              <a
                href={SETUP_CALL_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOnboardingEvent('flusia.onboarding.setup_call_click')}
                className="mx-2 mb-2 mt-1 px-2.5 py-2 rounded-md border border-dashed border-pink-200 dark:border-pink-900/40 bg-[#FFFBFD] dark:bg-pink-900/10 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors flex items-start gap-2 group"
              >
                <CalendarClock className="w-3.5 h-3.5 text-pink-500 mt-0.5 shrink-0" strokeWidth={2} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[12.5px] font-medium text-[#37352F] dark:text-white group-hover:text-pink-700 dark:group-hover:text-pink-300 transition-colors">
                    Preferisci che lo configuriamo noi?
                  </span>
                  <span className="block text-[11px] text-[#787774] dark:text-white/50 mt-0.5 leading-snug">
                    20 minuti con Luca, gratis: importiamo i contatti e impostiamo il primo invio.
                  </span>
                </span>
                <ArrowRight className="w-3 h-3 text-[#9B9A97] group-hover:text-pink-500 mt-1 shrink-0 transition-colors" strokeWidth={2} />
              </a>
            )}

            <div className="px-3 pb-2.5 pt-1 flex items-center justify-between gap-2 flex-wrap border-t border-[#EBEBEA] dark:border-white/10">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="text-[11px] text-[#9B9A97] hover:text-[#37352F] dark:hover:text-white inline-flex items-center gap-1 transition-colors"
                >
                  <X className="w-3 h-3" strokeWidth={2} />
                  Nascondi
                </button>
                {onDevReset && (
                  <button
                    type="button"
                    onClick={() => void onDevReset()}
                    className="text-[10px] text-[#9B9A97] hover:text-red-500 inline-flex items-center gap-1 transition-colors uppercase tracking-wider font-medium"
                    title="Dev only: reset onboarding completo (localStorage + DB)"
                  >
                    <RotateCcw className="w-2.5 h-2.5" strokeWidth={2} />
                    Reset dev
                  </button>
                )}
              </div>
              <WhatsAppSupportButton
                variant="inline"
                context="Sto configurando Flusia e ho una domanda"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`relative inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium transition-colors rounded-t-md ${
        active
          ? 'text-[#37352F] dark:text-white'
          : 'text-[#9B9A97] dark:text-white/40 hover:text-[#787774] dark:hover:text-white/70'
      }`}
    >
      {icon}
      {label}
      {active && (
        <motion.span
          layoutId="setup-tab-indicator"
          className="absolute left-2 right-2 -bottom-px h-[2px] rounded-t-full bg-gradient-to-r from-[#E91E8C] to-[#F97316]"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

function NotionCheckbox({ done }: { done: boolean }) {
  return (
    <span
      role="checkbox"
      aria-checked={done}
      className={`shrink-0 w-[16px] h-[16px] rounded-full inline-flex items-center justify-center transition-all ${
        done
          ? 'bg-gradient-to-br from-[#E91E8C] to-[#F97316] shadow-[0_1px_3px_rgba(233,30,140,0.35)]'
          : 'border border-[#D4D3CF] dark:border-white/25 bg-transparent group-hover:border-pink-400 dark:group-hover:border-pink-500'
      }`}
    >
      {done && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} aria-hidden="true" />}
    </span>
  );
}
